import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, FileText } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../services/api';
import { isPublicSolicitudContestada } from '../../utils/solicitudNotes';
import {
  buildSolicitudFormDisplayState,
  formatDateSpanishLong,
  formatDateTimeSpanishLong,
  formatDatoForField,
  formatPhoneDisplay,
  getDocDisplayName,
  getDocDownloadUrl,
  getDocMime,
  isDocImage,
  isDocPdf,
  labelizeKey,
} from '../../utils/solicitudFormDisplay';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Toast } from '../../components/ui/Toast';
import { usePermissions } from '../../hooks/usePermissions';
import { formatCurrencyMxn, resolveMonthlyRentForDisplay } from '../../utils/money';
import { DocumentViewer } from '../../components/documents/DocumentViewer';

type ApplicationDetailApi = {
  id: string;
  prospectId: string;
  unitNumber: string;
  submittedDate: string;
  status: 'pending' | 'approved' | 'rejected' | string;
  notes?: string;
  propertyId: string;
  properties?: { name?: string };
  prospects?: any; // incluye properties en backend
};

type ApplicationDetailTab = 'general' | 'informacion' | 'documentos';

export const ApplicationDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const canViewPrices = hasPermission('view_prices');
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [activeTab, setActiveTab] = useState<ApplicationDetailTab>('general');

  const {
    data: applicationRes,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['application-detail', id],
    queryFn: async () => {
      if (!id) throw new Error('ID inválido');
      return api.getApplication(id);
    },
    enabled: !!id,
    staleTime: 60_000,
  });

  const application = applicationRes as ApplicationDetailApi | undefined;
  const hasSolicitudContestada = useMemo(() => {
    return isPublicSolicitudContestada(application?.notes);
  }, [application?.notes]);

  useEffect(() => {
    if (!application || !id) return;
    if (hasSolicitudContestada) return;
    setToast({ message: 'La solicitud aún no está contestada. No se puede ver.', type: 'error' });
    navigate('/rentas/solicitudes', { replace: true });
  }, [application, id, hasSolicitudContestada, navigate]);

  const { data: unitsRes } = useQuery({
    queryKey: ['units-by-property', application?.propertyId],
    queryFn: async () => {
      if (!application?.propertyId) return [];
      return api.getUnits(application.propertyId);
    },
    enabled: !!application?.propertyId,
    staleTime: 60_000,
  });

  const unit = useMemo(() => {
    if (!application) return null;
    const list = Array.isArray(unitsRes) ? unitsRes : (unitsRes as any)?.data ?? [];
    return list.find((u: any) => u.unitNumber === application.unitNumber) || null;
  }, [application, unitsRes]);

  const { parsedNotes, datosFormulario, orderedKeys, docKeysWithValue } = useMemo(
    () => buildSolicitudFormDisplayState(application?.notes),
    [application?.notes],
  );

  const prospect = application?.prospects || null;
  const prospectName = prospect?.name || '-';
  const prospectEmail = prospect?.email || '-';
  const prospectPhone = prospect?.phone ? formatPhoneDisplay(String(prospect.phone)) : '-';
  const prospectPropertyName = prospect?.properties?.name || '-';

  const propertyName = application?.properties?.name || '-';
  const submittedDateLabel = useMemo(() => {
    if (!application?.submittedDate) return '-';
    return formatDateSpanishLong(new Date(application.submittedDate));
  }, [application?.submittedDate]);

  const statusLabel =
    application?.status === 'approved'
      ? 'Aprobada'
      : application?.status === 'pending'
        ? 'Pendiente'
        : application?.status === 'rejected'
          ? 'Rechazada'
          : application?.status || '-';

  const monthlyRentResolved = useMemo(
    () => resolveMonthlyRentForDisplay(unit, application?.notes),
    [unit, application?.notes],
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button
          icon={ArrowLeft}
          onClick={() => navigate('/rentas/solicitudes')}
          variant="secondary"
        >
          Volver
        </Button>
        <h1 className="text-2xl font-bold text-gray-900">Solicitud</h1>
      </div>

      {isLoading && <div className="text-gray-500 py-8 text-center">Cargando...</div>}

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 px-4 py-3">
          {String((error as any)?.message || 'Error al cargar la solicitud')}
        </div>
      )}

      {!isLoading && application && hasSolicitudContestada && (
        <div className="space-y-6">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8">
              {(
                [
                  { id: 'general' as const, label: 'General' },
                  { id: 'informacion' as const, label: 'Información' },
                  { id: 'documentos' as const, label: 'Documentos' },
                ] as const
              ).map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-accent text-accent'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {activeTab === 'general' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card title="Información del prospecto">
                <div className="space-y-4 text-sm">
                  <div>
                    <div className="text-gray-500">Nombre</div>
                    <div className="font-medium text-gray-900">{prospectName}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Email</div>
                    <div className="font-medium text-gray-900">{prospectEmail}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Teléfono</div>
                    <div className="font-medium text-gray-900">{prospectPhone}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Propiedad de interés</div>
                    <div className="font-medium text-gray-900">{prospectPropertyName}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Fecha de contacto</div>
                    <div className="font-medium text-gray-900">
                      {prospect?.contactDate
                        ? formatDateSpanishLong(new Date(prospect.contactDate))
                        : '-'}
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-500">Estado de aprobación</div>
                    <div className="font-medium text-gray-900">{prospect?.approvalStatus || '-'}</div>
                  </div>
                </div>
              </Card>

              <Card title="Resumen de la solicitud">
                <div className="space-y-4 text-sm">
                  <div>
                    <div className="text-gray-500">Propiedad</div>
                    <div className="font-medium text-gray-900">{propertyName}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Unidad</div>
                    <div className="font-medium text-gray-900">{application.unitNumber}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Monto mensual</div>
                    <div className="font-medium text-gray-900">
                      {canViewPrices && monthlyRentResolved != null
                        ? formatCurrencyMxn(monthlyRentResolved)
                        : '—'}
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-500">Fecha de solicitud</div>
                    <div className="font-medium text-gray-900">{submittedDateLabel}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Estado</div>
                    <div className="font-medium text-gray-900">{statusLabel}</div>
                  </div>
                </div>
              </Card>
            </div>
          )}

          {activeTab === 'informacion' && (
            <Card title="Información del formulario">
              <div className="space-y-4 text-sm">
                {parsedNotes ? (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <div className="text-gray-500">Tipo de persona</div>
                        <div className="font-medium text-gray-900">{parsedNotes?.tipoPersona || '-'}</div>
                      </div>
                      <div>
                        <div className="text-gray-500">Guardado</div>
                        <div className="font-medium text-gray-900">
                          {parsedNotes?.savedAt ? formatDateTimeSpanishLong(String(parsedNotes.savedAt)) : '-'}
                        </div>
                      </div>
                    </div>

                    <div>
                      <div className="font-semibold text-gray-900 mb-2">Contacto</div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <div className="text-gray-500">Nombre</div>
                          <div className="font-medium text-gray-900">{parsedNotes?.contacto?.name || '-'}</div>
                        </div>
                        <div>
                          <div className="text-gray-500">Correo</div>
                          <div className="font-medium text-gray-900">{parsedNotes?.contacto?.email || '-'}</div>
                        </div>
                        <div>
                          <div className="text-gray-500">Teléfono</div>
                          <div className="font-medium text-gray-900">
                            {parsedNotes?.contacto?.phone
                              ? formatPhoneDisplay(String(parsedNotes.contacto.phone))
                              : '-'}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div>
                      <div className="font-semibold text-gray-900 mb-2">Datos del formulario</div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {orderedKeys.map((key) => {
                          if (!Object.prototype.hasOwnProperty.call(datosFormulario, key)) return null;
                          const value = datosFormulario[key];
                          const isEmpty =
                            value === null ||
                            value === undefined ||
                            (typeof value === 'string' && value.trim() === '');
                          if (isEmpty) return null;
                          return (
                            <div key={key} className="break-words">
                              <div className="text-gray-500">{labelizeKey(key)}</div>
                              <div className="font-medium text-gray-900">{formatDatoForField(key, value)}</div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </>
                ) : (
                  <div>
                    <div className="text-gray-500 mb-2">Notas</div>
                    <pre className="whitespace-pre-wrap break-words text-sm bg-gray-50 border border-gray-200 rounded-lg p-3 max-h-96 overflow-auto">
                      {application.notes}
                    </pre>
                  </div>
                )}
              </div>
            </Card>
          )}

          {activeTab === 'documentos' && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h2 className="text-lg font-semibold">Documentos</h2>
                  <p className="text-sm text-gray-500 mt-1">
                    Documentos adjuntos a esta solicitud
                  </p>
                </div>
              </div>
              <Card>
                {docKeysWithValue.length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-500">No hay documentos adjuntos a esta solicitud</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {docKeysWithValue.map((key) => {
                      const value = datosFormulario[key];
                      const viewUrl = getDocDownloadUrl(value);
                      const displayName = getDocDisplayName(key, value);
                      const mime = getDocMime(value);
                      const isPdf = isDocPdf(value);
                      const isImage = isDocImage(value);
                      const kindLabel = isImage ? 'IMAGEN' : isPdf ? 'PDF' : mime ? mime.split('/').pop()?.toUpperCase() || 'DOC' : 'DOC';
                      const displayDate = parsedNotes?.savedAt
                        ? formatDateSpanishLong(new Date(parsedNotes.savedAt))
                        : application?.submittedDate
                          ? formatDateSpanishLong(new Date(application.submittedDate))
                          : '-';

                      if (!viewUrl) return null;
                      return (
                        <DocumentViewer
                          key={key}
                          title={displayName || labelizeKey(key)}
                          fileUrl={viewUrl}
                          fileTypeLabel={`${kindLabel} · ${displayDate}`}
                          downloadName={displayName || undefined}
                        />
                      );
                    })}
                  </div>
                )}
              </Card>
            </div>
          )}
        </div>
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

